import { useCallback, useEffect, useRef, useState } from 'react';
import utils from '@happylife-a/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useShoppingCarts as useShoppingCartsContext } from '../../../contexts/ShoppingCartContext';
import { useUser } from '../../../contexts/UserContext';
import { reduceCartsTotal } from '../../../helpers';
import { useIncreasedPrices } from '../../../hooks';
import ShoppingCartServiceV2 from '../../services/v2/ShoppingCartServiceV2';

const shoppingCartServiceV2 = ShoppingCartServiceV2();

export const REACT_QUERY_GET_CART_ITEMS_IDS_V2_QUERY_KEY = 'shopping-cart-items-ids-v2';
export const REACT_QUERY_GET_CART_ITEMS_V2_QUERY_KEY = 'shopping-cart-items-v2';

const QUERY_KEY_CART_IDS = [REACT_QUERY_GET_CART_ITEMS_IDS_V2_QUERY_KEY];
const QUERY_KEY_CART_ITEMS = [REACT_QUERY_GET_CART_ITEMS_V2_QUERY_KEY];

export const useGetCartItemsIdsV2 = (options = {}) => {
  const query = useQuery({
    queryKey: QUERY_KEY_CART_IDS,
    queryFn: shoppingCartServiceV2.getCartItemsIds,
    ...options,
  });

  return {
    ...query,
    data: query.data || {},
  };
};

export const useShoppingCartsV2 = (isInCart, { setIsCostCalculating } = {}) => {
  const { user, setOptimisticUpdate } = useUser();
  const queryClient = useQueryClient();
  const { setCartItemsTotal } = useShoppingCartsContext();
  const { increase } = useIncreasedPrices();

  const { mutate: deleteCart } = useMutation({ mutationFn: shoppingCartServiceV2.deleteCart });
  const { mutate: createCart } = useMutation({ mutationFn: shoppingCartServiceV2.createCart });
  const { mutate: updateCart } = useMutation({
    mutationFn: shoppingCartServiceV2.updateCartCount,
  });
  const { mutate: bulkDeleteCart } = useMutation({
    mutationFn: shoppingCartServiceV2.bulkDeleteCartItems,
  });

  const updateCartItemsData = useCallback(
    (callback) => {
      queryClient.setQueriesData({ queryKey: QUERY_KEY_CART_ITEMS }, (data) => {
        const newData = callback({ ...data });
        setCartItemsTotal(reduceCartsTotal(newData.shoppingCarts, increase));
        return newData;
      });
    },
    [setCartItemsTotal, increase]
  );

  const cartsCachedData = queryClient.getQueryData(QUERY_KEY_CART_IDS);
  const [carts, setCarts] = useState(cartsCachedData);

  const updateCartIdsData = useCallback(
    (callback) => {
      queryClient.setQueriesData({ queryKey: QUERY_KEY_CART_IDS }, (data) => {
        const newData = callback({ ...data });
        newData.total = newData.ids.reduce((acc, item) => acc + item.quantity, 0);

        setCarts(newData);
        return newData;
      });
    },
    [queryClient]
  );

  useEffect(() => {
    setCarts(cartsCachedData);
  }, [cartsCachedData]);

  // Use ref to avoid stale carts in callbacks — same pattern as FavoriteContext
  const refCarts = useRef();
  refCarts.current = carts;

  const toggleCart = useCallback(
    (productVariantId, { onCreate, onDelete, count } = {}) => {
      if (isInCart(productVariantId)) {
        const cartRecord = refCarts.current?.ids?.find((item) =>
          utils.helpers.id.same(item.productVariantId, productVariantId)
        );

        if (!cartRecord?.id) {
          return;
        }

        const onDeleteCartItemSuccess = () => {
          updateCartIdsData((data) => ({
            ...data,
            ids: data.ids.filter(
              (item) => !utils.helpers.id.same(item.productVariantId, productVariantId)
            ),
          }));

          let lastObjectInfo = null;
          updateCartItemsData((data) => ({
            ...data,
            meta: { ...data.meta, total: data.meta.total - 1 },
            shoppingCarts: data.shoppingCarts?.filter((item) => {
              const isRemoved = utils.helpers.id.same(item.productVariantId, productVariantId);
              if (isRemoved) {
                lastObjectInfo = item;
              }
              return !isRemoved;
            }),
          }));

          if (typeof onDelete === 'function') {
            onDelete(lastObjectInfo);
          }
        };

        deleteCart({ id: cartRecord.id }, { onSuccess: onDeleteCartItemSuccess });
      } else {
        const createCartInput = {
          productVariantId: productVariantId,
          quantity: count || 1,
        };

        const onCreateSuccess = (response) => {
          const lastObjectInfo = {
            id: response.id,
            productId: response.productId,
            productVariantId: response.productVariantId,
            productDetailId: response.productVariantId,
            quantity: response.quantity,
            productDetail: {
              price: increase(response.productVariant?.price),
              discountedPrice: increase(response.productVariant?.discountedPrice),
            },
          };

          updateCartIdsData((data) => ({
            ...data,
            ids: [...data.ids, lastObjectInfo],
          }));
          updateCartItemsData((data) => ({
            ...data,
            meta: { ...data.meta, total: data.meta.total + 1 },
            shoppingCarts: [response, ...(data?.shoppingCarts || [])],
          }));

          if (typeof onCreate === 'function') {
            onCreate(lastObjectInfo);
          }
        };

        createCart(createCartInput, { onSuccess: onCreateSuccess });
      }
    },
    [isInCart, deleteCart, createCart, increase, updateCartIdsData, updateCartItemsData]
  );

  const updateCartCount = useCallback(
    (cart, { onSuccessUpdate } = {}) => {
      const cartRecord = refCarts.current?.ids?.find((item) =>
        utils.helpers.id.same(item.productVariantId, cart.productDetailId)
      );

      if (!cartRecord?.id) {
        return;
      }

      const input = {
        id: cartRecord.id,
        quantity: cart.quantity,
      };

      const setUpdateStatus = (isUpdating) => {
        if (typeof setIsCostCalculating === 'function') {
          setIsCostCalculating(isUpdating);
        }
        if (typeof onSuccessUpdate === 'function') {
          onSuccessUpdate(isUpdating);
        }
      };

      const onUpdateSuccess = (response) => {
        updateCartItemsData((data) => {
          if (!data) {
            return data;
          }
          return {
            ...data,
            shoppingCarts: data.shoppingCarts.map((item) => {
              if (utils.helpers.id.same(item.id, response.id)) {
                return { ...item, quantity: response.quantity };
              }
              return item;
            }),
          };
        });

        updateCartIdsData((data) => {
          if (!data) return data;
          return {
            ...data,
            ids: data.ids.map((item) => {
              if (utils.helpers.id.same(item.productVariantId, cart.productDetailId)) {
                return { ...item, quantity: response.quantity };
              }
              return item;
            }),
          };
        });

        setUpdateStatus(false);
      };

      setUpdateStatus(true);
      updateCart(input, { onSuccess: onUpdateSuccess });
    },
    [updateCart, setIsCostCalculating, updateCartItemsData, updateCartIdsData]
  );

  const bulkDeleteCarts = useCallback(
    (productDetailIds, { onSuccessDelete, onErrorDelete }) => {
      const productVariantIds = productDetailIds;

      const onBulkDeleteCartItemsSuccess = () => {
        queryClient.setQueriesData({ queryKey: QUERY_KEY_CART_IDS }, (data) => {
          return {
            ...data,
            ids: data.ids.filter((item) => !productVariantIds.includes(item.productVariantId)),
          };
        });

        let lastObjectInfo = null;
        updateCartItemsData((data) => ({
          meta: { ...data.meta, total: data.meta.total - productVariantIds.length },
          shoppingCarts: data.shoppingCarts.filter((item) => {
            const isRemoved = productVariantIds.includes(item.productVariantId);
            if (isRemoved) {
              lastObjectInfo = item;
            }
            return !isRemoved;
          }),
        }));

        if (typeof onSuccessDelete === 'function' && lastObjectInfo) {
          onSuccessDelete(lastObjectInfo);
        }
      };

      const onErrorBulkDelete = (e) => {
        if (typeof onErrorDelete === 'function') {
          onErrorDelete(e.message);
        }
      };

      bulkDeleteCart(
        { productVariantIds: productVariantIds },
        {
          onSuccess: onBulkDeleteCartItemsSuccess,
          onError: onErrorBulkDelete,
        }
      );
    },
    [bulkDeleteCart, queryClient, updateCartItemsData]
  );

  useEffect(() => {
    if (!carts) {
      return;
    }

    const shoppingCartsCount = (carts.ids || []).reduce((acc, cart) => acc + cart.quantity, 0);

    setOptimisticUpdate({
      ...user,
      customerInfo: {
        ...user.customerInfo,
        shoppingCartsCount: shoppingCartsCount,
      },
    });
  }, [carts]);

  return {
    toggleCart: toggleCart,
    updateCartCount: updateCartCount,
    bulkDeleteCarts: bulkDeleteCarts,
  };
};
